// AddModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  ADD_MODAL_IMMEDIATE_RELATED_TYPE_KEYS,
  ADD_MODAL_RELATED_STEPS,
  ADD_MODAL_TABS,
} from 'components/addmodal/utilities/constants';
import { getSingularLabel } from 'utilities/Languages';

// Hooks
import { useCreateTypeForm } from 'components/addmodal/sections/createtype/useCreateTypeForm';
import { useFindExisting } from 'components/addmodal/sections/find/useFindExisting';

// CSS
import './AddModal.css';

// Components
import CreateNewSection from 'components/addmodal/sections/createnew/CreateNewSection';
import FindExistingSection from 'components/addmodal/sections/find/FindExistingSection';
import CreateTypeSection from 'components/addmodal/sections/createtype/CreateTypeSection';
import LinkOrCreateSection from 'components/addmodal/sections/linkorcreate/LinkOrCreateSection';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import Tabs from 'components/tabs/Tabs';

/**
 * AddModal
 *
 * This component renders a modal for adding context to a node. The default view
 * allows selecting existing base types (or attaching existing nodes via search).
 * Top-level New can also show base launchers (Database, Message, Channel, Dashboard)
 * that open the same create modals as the sidebar "+ New" buttons.
 * Media capture/upload are consolidated to Camera and Files.
 *
 * @param {Object} node - The node to which context is being added.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Function to call when the modal is closed.
 * @param {function} onTypeSelect - Function to call when a type or launcher is selected.
 * @param {function} onExistingAttached - Function to call when an existing node is attached.
 * @param {string|null} anchorNodeKey - The key of the anchor node, if applicable.
 * @param {boolean} createOnly - When true, hide find-existing / More / injected types (type picker only).
 * @param {boolean} showBaseLaunchers - When true, show Database/Channel/Message/Dashboard launchers (top-level New).
 * @param {boolean} libraryInfoMode - Project Information “Add More Info” Capture picker.
 * @param {boolean} [allowCreate=true] - When false, hide CREATE NEW in link-or-create (link existing only).
 * @param {boolean} [findOnly=false] - Search-only modal for linking an existing item.
 * @param {string|null} [initialRelatedTypeKey=null] - Skip type picker; open link-or-create for this type.
 * @param {string[]|null} [relatedTypeKeys=null] - Optional multi-type attach list (e.g. Drive PHOTO/DOCUMENT/FILE).
 * @param {string} [attachSourceType=''] - Optional connector filter for attach lists (calendar/drive/…).
 * @param {string} [attachListLabel=''] - Optional plural label override for attach lists.
 * @param {function|null} typeFilter - Optional predicate to filter available types.
 * @param {string|null} title - Optional modal title override.
 *
 * @returns {ReactNode} The rendered component.
 */
const AddModal = React.memo(({
    insertMode = false,
    createOnly = false,
    showBaseLaunchers = false,
    libraryInfoMode = false,
    allowCreate = true,
    findOnly = false,
    initialRelatedTypeKey = null,
    relatedTypeKeys = null,
    attachSourceType = '',
    attachListLabel = '',
    typeFilter = null,
    title = null,
    node,
    isOpen,
    onClose,
    onExistingSelected,
    onTypeSelect,
    onExistingAttached,
    anchorNodeKey = null,
    showInjectedAddTypes = false,
}) => {

    // ----- CONTEXT ----- //

    // Node Types Context

    const { getAll, getType } = useNodeTypes();

    // User Context
    const { user } = useUser();

    // Base Context
    const { activeBase } = useBase();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [isMorePanelOpen, setIsMorePanelOpen] = useState(false);
    const [morePanelTab, setMorePanelTab] = useState(ADD_MODAL_TABS.TYPE);
    const [relatedStep, setRelatedStep] = useState(ADD_MODAL_RELATED_STEPS.TYPE);
    const [selectedLinkTypeKey, setSelectedLinkTypeKey] = useState('');

    const isRelatedLinkFlow = Boolean(showInjectedAddTypes && anchorNodeKey && !insertMode);

    // Base Key
    const baseKey = activeBase?.key || null;

    // User Key
    const userKey = user?.key || null;

    // Type Definitions
    const typeDefs = useMemo(() => getAll() || [], [getAll]);

    // ----- MEMOS ----- //

    // Sorted Types (optionally filtered for callers like calendar create)

    const availableTypes = useMemo(() => {
        const rows = [...typeDefs].sort((a, b) => (
            String(a?.label || '').localeCompare(String(b?.label || ''))
        ));
        if (typeof typeFilter !== 'function') return rows;
        return rows.filter((typeDef) => {
            try {
                return Boolean(typeFilter(typeDef));
            } catch {
                return false;
            }
        });
    }, [typeDefs, typeFilter]);

    // Map of available types for easy lookup by key in search results
    const availableTypeMap = useMemo(() => {
        const map = new Map();
        availableTypes.forEach((typeDef) => {
            if (typeDef?.key) map.set(typeDef.key, typeDef);
        });
        return map;
    }, [availableTypes]);

    const showMorePanel = !insertMode && !createOnly && !showBaseLaunchers && !findOnly && isMorePanelOpen;

    // Active tab value passed to section hooks for gating data loading/search.
    const activePanelTab = findOnly
        ? ADD_MODAL_TABS.FIND
        : (isMorePanelOpen ? morePanelTab : ADD_MODAL_TABS.CREATE);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;

        setIsMorePanelOpen(false);
        setMorePanelTab(ADD_MODAL_TABS.TYPE);

        const presetTypeKey = String(initialRelatedTypeKey || '').trim();
        if (presetTypeKey) {
            setSelectedLinkTypeKey(presetTypeKey);
            setRelatedStep(ADD_MODAL_RELATED_STEPS.LINK);
            return;
        }

        setRelatedStep(ADD_MODAL_RELATED_STEPS.TYPE);
        setSelectedLinkTypeKey('');
    }, [initialRelatedTypeKey, isOpen]);

    // ----- MEMOS ----- //

    const isImmediateRelatedType = useCallback((typeKey) => {
        const normalizedTypeKey = String(typeKey || '').trim();
        if (!normalizedTypeKey) return false;

        return ADD_MODAL_IMMEDIATE_RELATED_TYPE_KEYS.has(normalizedTypeKey.toUpperCase());
    }, []);

    // ----- HANDLERS ----- //

    const handleRelatedTypeSelect = useCallback((typeKey, options = {}) => {
        if (isImmediateRelatedType(typeKey)) {
            onTypeSelect?.(typeKey, options);
            onClose?.();
            return;
        }

        setSelectedLinkTypeKey(typeKey);
        setRelatedStep(ADD_MODAL_RELATED_STEPS.LINK);
    }, [isImmediateRelatedType, onClose, onTypeSelect]);

    const handleRelatedLinkBack = useCallback(() => {
        setRelatedStep(ADD_MODAL_RELATED_STEPS.TYPE);
        setSelectedLinkTypeKey('');
    }, []);

    const handleMorePanelBack = useCallback(() => {
        setIsMorePanelOpen(false);
    }, []);

    // ----- MEMOS ----- //

    const selectedLinkTypeDef = useMemo(() => (
        selectedLinkTypeKey ? getType(selectedLinkTypeKey) : null
    ), [getType, selectedLinkTypeKey]);

    const selectedLinkTypeLabel = useMemo(() => (
        getSingularLabel(selectedLinkTypeDef, selectedLinkTypeKey || 'Item', 'en')
    ), [selectedLinkTypeDef, selectedLinkTypeKey]);

    const modalTitle = useMemo(() => {
        if (typeof title === 'string' && title.trim()) return title.trim();
        if (findOnly) return 'Attach…';
        if (libraryInfoMode) return 'Add';
        if (isRelatedLinkFlow && relatedStep === ADD_MODAL_RELATED_STEPS.LINK && node?.title) {
            return `Link ${selectedLinkTypeLabel} to '${node.title}'`;
        }
        if (node?.title) return `Link to '${node.title}'`;
        return 'New';
    }, [findOnly, isRelatedLinkFlow, libraryInfoMode, node?.title, relatedStep, selectedLinkTypeLabel, title]);

    const modalOnBack = useMemo(() => {
        // Direct entry into a typed link step (e.g. Attach ClickUp Tasks) — no type picker to return to.
        if (String(initialRelatedTypeKey || '').trim()) {
            return undefined;
        }
        if (isRelatedLinkFlow && relatedStep === ADD_MODAL_RELATED_STEPS.LINK) {
            return handleRelatedLinkBack;
        }
        if (!insertMode && !createOnly && !showBaseLaunchers && isMorePanelOpen) {
            return handleMorePanelBack;
        }
        return undefined;
    }, [
        createOnly,
        handleMorePanelBack,
        handleRelatedLinkBack,
        initialRelatedTypeKey,
        insertMode,
        isMorePanelOpen,
        isRelatedLinkFlow,
        relatedStep,
        showBaseLaunchers,
    ]);

    // ----- HOOKS ----- //

    // Find Existing Hook

    const {
        searchTerm,
        setSearchTerm,
        searchResults,
        searching,
        searchError,
        attachingNodeKey,
        handleAttachExisting,
        noResults,
    } = useFindExisting({
        enableSelectionWithoutAnchor: insertMode,
        isOpen,
        showFind: insertMode || findOnly,
        selectedTab: activePanelTab,
        findTabValue: findOnly ? ADD_MODAL_TABS.FIND : ADD_MODAL_TABS.CREATE,
        baseKey,
        anchorNodeKey,
        anchorNode: node,
        onAttached: insertMode ? onExistingSelected : onExistingAttached,
        onClose,
    });

    // Create Type Form Hook
    const {
        label,
        setLabel,
        labelPlural,
        description,
        setDescription,
        iconKey,
        setIconKey,
        defaultView,
        setDefaultView,
        isSavingType,
        canSaveType,
        labelHelperText,
        pluralHelperText,
        handleLabelPluralChange,
        handleLabelBlur,
        handleLabelPluralBlur,
        handleCreateType,
    } = useCreateTypeForm({
        isOpen,
        typeDefs,
        userKey,
        baseKey,
        anchorNodeKey,
        onTypeSelect,
        onClose,
    });

    const moreTabs = [
        // Store v1: app-only install replaces single-database library import.
        // Kept commented for possible reuse during _library transition.
        // {
        //     key: ADD_MODAL_TABS.LIBRARY,
        //     label: 'From Library',
        //     onClick: () => setMorePanelTab(ADD_MODAL_TABS.LIBRARY),
        // },
        {
            key: ADD_MODAL_TABS.TYPE,
            label: 'Create New...',
            onClick: () => setMorePanelTab(ADD_MODAL_TABS.TYPE),
        },
    ];

    // ----- HANDLERS ----- //

    /**
     * handleOpenMorePanel
     *
     * Handles open more panel.
     */
    const handleOpenMorePanel = () => {
        if (insertMode || createOnly || showBaseLaunchers) return;
        setMorePanelTab(ADD_MODAL_TABS.TYPE);
        setIsMorePanelOpen(true);
    };

    // ----- RENDER ----- //

    return (
        <Modal
          iconKey={insertMode ? 'article' : node?.title ? 'link' : 'add'}
          title={modalTitle}
          isOpen={isOpen}
          width={isMobile ? '100%' : 500}
          height={
            findOnly
              || (isRelatedLinkFlow && relatedStep === ADD_MODAL_RELATED_STEPS.LINK)
              ? '80%'
              : 'auto'
          }
          maxHeight="80%"
          onBack={modalOnBack}
          onClose={onClose}
        >
            <ModalForm
              height={findOnly || isRelatedLinkFlow ? '100%' : 'auto'}
              containScroll={findOnly || isRelatedLinkFlow}
            >

                {findOnly && (
                    <FindExistingSection
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      searchError={searchError}
                      searching={searching}
                      noResults={noResults}
                      searchResults={searchResults}
                      availableTypeMap={availableTypeMap}
                      anchorNodeKey={anchorNodeKey}
                      attachingNodeKey={attachingNodeKey}
                      onAttachExisting={handleAttachExisting}
                    />
                )}

                {!findOnly && !isMorePanelOpen && isRelatedLinkFlow && relatedStep === ADD_MODAL_RELATED_STEPS.LINK && (
                    <LinkOrCreateSection
                      isOpen={isOpen}
                      typeKey={selectedLinkTypeKey}
                      typeKeys={relatedTypeKeys}
                      sourceType={attachSourceType}
                      listLabel={attachListLabel}
                      anchorNode={node}
                      baseKey={baseKey}
                      user={user}
                      allowCreate={allowCreate}
                      onAttached={onExistingAttached}
                      onClose={onClose}
                    />
                )}

                {!findOnly && !showMorePanel && !(isRelatedLinkFlow && relatedStep === ADD_MODAL_RELATED_STEPS.LINK) && (
                    <CreateNewSection
                      availableTypes={availableTypes}
                      enableSelectionWithoutAnchor={insertMode}
                      showMoreCard={!insertMode && !createOnly && !isRelatedLinkFlow && !showBaseLaunchers && !libraryInfoMode}
                      showBaseLaunchers={showBaseLaunchers && !insertMode && !createOnly && !isRelatedLinkFlow && !libraryInfoMode}
                      showMediaActions={!createOnly && !libraryInfoMode}
                      libraryInfoMode={libraryInfoMode}
                      relatedTypeSelectMode={isRelatedLinkFlow}
                      showSearchInput={Boolean(insertMode)}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      searchError={searchError}
                      searching={searching}
                      noResults={noResults}
                      searchResults={searchResults}
                      availableTypeMap={availableTypeMap}
                      anchorNodeKey={anchorNodeKey}
                      attachingNodeKey={attachingNodeKey}
                      onAttachExisting={handleAttachExisting}
                      onClose={onClose}
                      onMoreClick={handleOpenMorePanel}
                      onTypeSelect={isRelatedLinkFlow ? handleRelatedTypeSelect : onTypeSelect}
                    />
                )}

                {showMorePanel && (
                    <div className="add-modal-more-panel">

                        {/* ACTIONS */}
                        <div className="add-modal-actions">
                            <Tabs
                              tabs={moreTabs}
                              selectedKey={morePanelTab}
                            />
                        </div>

                        {/* Store v1: From Library tab hidden — see moreTabs above.
                        {morePanelTab === ADD_MODAL_TABS.LIBRARY && (
                            <LibrarySection
                                query={libraryQuery}
                                setQuery={setLibraryQuery}
                                loading={libraryLoading}
                                error={libraryError}
                                terms={libraryTerms}
                                selectedTerm={librarySelectedTerm}
                                selectedAddon={librarySelectedAddon}
                                isAddonStep={libraryIsAddonStep}
                                isAddonPreviewStep={libraryIsAddonPreviewStep}
                                isProvisioning={libraryIsProvisioning}
                                provisioningAddonKey={provisioningAddonKey}
                                selectedFieldKeys={librarySelectedFieldKeys}
                                baseKey={libraryBaseKey}
                                onSelectTerm={handleSelectTerm}
                                onSelectAddon={handleSelectAddon}
                                onToggleSelectedField={handleToggleSelectedField}
                                onBackToTerms={handleBackToTerms}
                                onBackToAddons={handleBackToAddons}
                                onUseSelectedAddon={handleUseSelectedAddon}
                            />
                        )}
                        */}

                        {morePanelTab === ADD_MODAL_TABS.TYPE && (
                            <CreateTypeSection
                              iconKey={iconKey}
                              setIconKey={setIconKey}
                              isSavingType={isSavingType}
                              canSaveType={canSaveType}
                              label={label}
                              setLabel={setLabel}
                              labelHelperText={labelHelperText}
                              labelPlural={labelPlural}
                              pluralHelperText={pluralHelperText}
                              description={description}
                              setDescription={setDescription}
                              defaultView={defaultView}
                              setDefaultView={setDefaultView}
                              onCreateType={handleCreateType}
                              onLabelBlur={handleLabelBlur}
                              onLabelPluralBlur={handleLabelPluralBlur}
                              onLabelPluralChange={handleLabelPluralChange}
                            />
                        )}
                    </div>
                )}

            </ModalForm>

            {/* Store v1: From Library provisioning footer hidden — see LibrarySection above.
            {!insertMode && isMorePanelOpen && morePanelTab === ADD_MODAL_TABS.LIBRARY && libraryIsAddonPreviewStep && (
                <ModalButtons>
                    <Button
                        iconKey="check"
                        enabled={!libraryIsProvisioning}
                        label={libraryIsProvisioning ? 'Provisioning...' : 'Continue'}
                        onClick={handleUseSelectedAddon}
                    />
                </ModalButtons>
            )}
            */}

        </Modal>
    );
});

// Dev Tools
AddModal.displayName = 'AddModal';

export default AddModal;
